import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CloseButton } from '#src/atoms/buttons/CloseButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, LabelM } from '#src/atoms/typography/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useApplyJonumRestriction } from '#src/hooks/useApplyJonumRestriction.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { thinMaterial } from '#src/style/utils.tsx';

import type {
  CreateJonumVerificationDisclaimerMutation,
  CreateJonumVerificationDisclaimerMutationVariables,
} from './__generated__/index.graphql.ts';

const CREATE_JONUM_VERIFICATION_MUTATION = gql`
  mutation CreateJonumVerificationDisclaimerMutation(
    $input: createJonumVerificationInput!
  ) {
    createJonumVerification(input: $input) {
      currentUser {
        slug
        jonumVerification {
          id
          status
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  CreateJonumVerificationDisclaimerMutation,
  CreateJonumVerificationDisclaimerMutationVariables
>;

const Banner = styled(Horizontal).attrs({ gap: 2 })`
  ${thinMaterial}
  position: sticky;
  bottom: calc(var(--double-unit) + var(--bottom-bar-height-mobile));
  margin: var(--double-unit) var(--double-unit) 0;
  padding: var(--double-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
`;

const InfoIcon = styled(FontAwesomeIcon)`
  color: var(--c-nd-600);
  flex-shrink: 0;
  margin-top: 2px;
`;

export const JonumVerificationDisclaimer = () => {
  const [dismissed, setDismissed] = useState(false);
  const {
    flags: { jonumUserInfoCollection = false },
  } = useFeatureFlags();
  const isFrance = useApplyJonumRestriction();
  const mutationCalledRef = useRef(false);
  const { currentUser, refetch } = useCurrentUserContext();

  const [createJonumVerification] = useMutation(
    CREATE_JONUM_VERIFICATION_MUTATION,
    {
      showErrorsWithSnackNotification: false,
    }
  );

  const hasVerification = !!currentUser?.jonumVerification;
  const notConcerned =
    !jonumUserInfoCollection || !currentUser || hasVerification;

  useEffect(() => {
    if (notConcerned || isFrance || mutationCalledRef.current) {
      return;
    }
    mutationCalledRef.current = true;
    createJonumVerification({ variables: { input: {} } }).then(async () =>
      refetch()
    );
  }, [notConcerned, isFrance, createJonumVerification, refetch]);

  if (notConcerned || !isFrance || dismissed) {
    return null;
  }

  return (
    <Banner>
      <InfoIcon icon={iconInfoCircle} />
      <Vertical gap={0} grow>
        <LabelM>
          <FormattedMessage defaultMessage="Managers based in France" />
        </LabelM>
        <BodyS>
          <FormattedMessage defaultMessage="You will be asked to verify your account, in accordance with French regulations." />
        </BodyS>
      </Vertical>
      <CloseButton
        transparent
        size="compact"
        onClose={() => setDismissed(true)}
      />
    </Banner>
  );
};
